import {
  CircleIcon,
  GearSixIcon,
  ListChecksIcon,
  WarningCircleIcon,
  WavesIcon,
  WifiSlashIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';

import { Chip, type ChipIntent } from '@/components/common/chip';

import { DEVICE_STATUS } from '../constants';
import type { Device } from '../types';

const ICON_SIZE = 10;

type ChipSpec = { label: string; intent: ChipIntent; icon: ReactNode };

/**
 * Status badge colorways from the device listing (Figma 232:350661) and details header
 * (Figma 219:327388). Every colorway resolves to a `<Chip>` intent — `idle` and `expired` were
 * added to the shared component for these two states.
 *
 * `expired` is in this table even though the backend never sends it as an `effectiveStatus`:
 * `toDevice` folds an expired subscription into it, because US-112437 §19 is explicit that an
 * expired device shows "Expired" and *no* live status.
 *
 * Exported because the Map's **Device quick view** renders the same pill from the same colorways
 * (113588 §5a, §17b): two tables would be two chances to disagree.
 */
export const DEVICE_STATUS_CHIP: Record<Device['status'], ChipSpec> = {
  [DEVICE_STATUS.RUNNING]: {
    label: 'Running',
    intent: 'published',
    icon: <WavesIcon size={ICON_SIZE} />,
  },
  [DEVICE_STATUS.OFF]: {
    label: 'Off',
    intent: 'idle',
    icon: <CircleIcon size={ICON_SIZE} weight="fill" />,
  },
  [DEVICE_STATUS.DISCONNECTED]: {
    label: 'Disconnected',
    intent: 'attention',
    icon: <WifiSlashIcon size={ICON_SIZE} />,
  },
  [DEVICE_STATUS.EXPIRED]: {
    label: 'Expired',
    intent: 'expired',
    icon: <WarningCircleIcon size={ICON_SIZE} />,
  },
  [DEVICE_STATUS.PENDING_QC]: {
    label: 'Pending QC',
    intent: 'review',
    icon: <ListChecksIcon size={ICON_SIZE} />,
  },
  [DEVICE_STATUS.PENDING_CONFIGURATION]: {
    label: 'Pending Configuration',
    intent: 'neutral',
    icon: <GearSixIcon size={ICON_SIZE} />,
  },
};

export type DeviceStatusChipProps = {
  device: Device;
  className?: string;
};

/** The single status badge shown per device. */
export function DeviceStatusChip({ device, className }: DeviceStatusChipProps) {
  const spec = DEVICE_STATUS_CHIP[device.status];

  return (
    <Chip intent={spec.intent} icon={spec.icon} className={className}>
      {spec.label}
    </Chip>
  );
}
